import { useEffect, useMemo, useState } from 'react';
import { demeterApi } from '../../../../Apis/Apis';
import { CommodityOtcPriceModel, Currency, DemeterTableDefinitionType, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import useCacheThenApi from '../../../Apis/Hooks/useCacheThenApiHook';
import { IOtcPriceSelection, toOtcSubRegion } from '../../../Apis/Hooks/useOtcPricesApiHook';
import useTableDefinition from '../../../Components/Navigation/Hooks/useTableDefinitionHook';
import CacheKeys from '../../../Services/Cache/CacheKeys';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { IOtcPricesChartProps } from '../../Futures/Otc/OtcPricesChart';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';

const defaultSelectedRowIndex = 0;

const withOtcPricesChartFromPageElement = (OtcPricesChart: React.ComponentType<IOtcPricesChartProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const [, translate] = useLanguage();
        const [otcPricesSelections, setOtcPricesSelections] = useState<IOtcPriceSelection[]>([]);
        const [currency, setCurrency] = useState<Currency | undefined>(props.pageElement.currency);
        const [unitOfMeasure, setUnitOfMeasure] = useState<UnitOfMeasure | undefined>(props.pageElement.unitOfMeasure);
        const { demeterPageElementGuid, commodity, region, extraParameters } = props.pageElement;
        const subRegion = toOtcSubRegion(props.pageElement.subRegion);
        const cacheKey = `${CacheKeys.ListCommodityOtcPricesCurrent}_${region}_${commodity}_${subRegion}_${currency}_${unitOfMeasure}_dashboard`;
        const [, , apiResponse] = useCacheThenApi(cacheKey, () =>
            demeterApi.listCommodityOtcPricesCurrent(
                region as string,
                commodity as string,
                subRegion,
                currency ?? Currency.Eur,
                unitOfMeasure ?? UnitOfMeasure.MetricTon,
            ),
        );

        const regionCommoditySelection = useMemo(
            () => ({
                region: (region as string) || '',
                subRegion: subRegion ?? '',
                commodity: (commodity as string) || '',
                extraParameters: extraParameters || '',
                dataFrequency: '',
            }),
            [region, subRegion, commodity, extraParameters],
        );

        const [, tableDefinitionCommodity] = useTableDefinition(DemeterTableDefinitionType.CommodityOtcPricesTable, regionCommoditySelection);

        useEffect(() => {
            if (apiResponse?.rows && apiResponse.rows.length > 0) {
                const rows = apiResponse.rows.slice(0, 10).map((row: CommodityOtcPriceModel, index: number) => ({
                    ...row,
                    id: `${index}`,
                    rowIndex: index,
                    selected: true,
                }));

                if (rows.length) {
                    setCurrency(rows[0].currency);
                    setUnitOfMeasure(rows[0].unitOfMeasure);
                }
                setOtcPricesSelections(rows);
            }
        }, [apiResponse]);

        const title = useMemo(() => {
            if (tableDefinitionCommodity) {
                return translate(tableDefinitionCommodity.displayName) || '';
            }
            return '';
        }, [tableDefinitionCommodity, translate]);

        const chartProps: IOtcPricesChartProps = useMemo(
            () => ({
                title,
                customTitle: props.customTitle,
                currency: currency ?? Currency.Eur,
                unitOfMeasure: unitOfMeasure ?? UnitOfMeasure.MetricTon,
                otcPricesSelections,
                displayDecimalPlacesMinimum: tableDefinitionCommodity?.displayDecimalPlacesMinimum ?? 0,
                displayDecimalPlacesMaximum: tableDefinitionCommodity?.displayDecimalPlacesMaximum ?? 0,
                testId: `DashboardOtcPricesChart-${demeterPageElementGuid}`,
                dragIndicatorIcon: props.dragIndicatorIcon,
                isInDashboardEditMode: props.isInDashboardEditMode,
                maximizeMinimizeButton: props.maximizeMinimizeButton,
                maxWidthHeightRatio: props.maxWidthHeightRatio,
                handleMoreOptionsSelection: props.handleMoreOptionsSelection,
                visibleIndexes: [defaultSelectedRowIndex],
                track: props.track,
            }),
            [otcPricesSelections, props, unitOfMeasure, currency],
        );

        return <OtcPricesChart {...chartProps} />;
    };

    return Wrapped;
};

export default withOtcPricesChartFromPageElement;
